const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):(00|15|30|45)$/;

type Duration = 30 | 45 | 60;

export interface WizardSearch {
  practitionerId?: string;
  date?: string;
  startTime?: string;
  durationMinutes?: Duration;
  sessionMode?: 'online' | 'in_person';
  sessionType?: 'new' | 'follow_up';
  /** Phase 2 — free-text booking note, optional, max 500 chars. Stored in
   *  URL search params so refresh / back doesn't lose what the client typed. */
  note?: string;
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function asDuration(v: unknown): Duration | undefined {
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : NaN;
  if (n === 30 || n === 45 || n === 60) return n;
  return undefined;
}

function asDate(v: unknown): string | undefined {
  const s = asString(v);
  return s !== undefined && DATE_RE.test(s) ? s : undefined;
}

function asTime(v: unknown): string | undefined {
  const s = asString(v);
  return s !== undefined && TIME_RE.test(s) ? s : undefined;
}

function asMode(v: unknown): 'online' | 'in_person' | undefined {
  return v === 'online' || v === 'in_person' ? v : undefined;
}

function asType(v: unknown): 'new' | 'follow_up' | undefined {
  return v === 'new' || v === 'follow_up' ? v : undefined;
}

/**
 * Parse and sanitise raw search-param input from a TanStack Router
 * `validateSearch` callback. Drops invalid fields silently — the wizard's
 * `beforeLoad` redirects to the earliest unsatisfied step instead of
 * surfacing parse errors.
 */
export function parseWizardSearch(input: Record<string, unknown>): WizardSearch {
  const out: WizardSearch = {};
  const pid = asString(input.practitionerId);
  if (pid !== undefined) out.practitionerId = pid;
  const date = asDate(input.date);
  if (date !== undefined) out.date = date;
  const startTime = asTime(input.startTime);
  if (startTime !== undefined) out.startTime = startTime;
  const dur = asDuration(input.durationMinutes);
  if (dur !== undefined) out.durationMinutes = dur;
  const mode = asMode(input.sessionMode);
  if (mode !== undefined) out.sessionMode = mode;
  const type = asType(input.sessionType);
  if (type !== undefined) out.sessionType = type;
  const note = asString(input.note);
  if (note !== undefined) out.note = note.slice(0, 500);
  return out;
}

export function requirePractitioner(s: WizardSearch): string {
  if (s.practitionerId === undefined) {
    throw new Error('Missing practitionerId');
  }
  return s.practitionerId;
}

export interface SlotPrereqs {
  practitionerId: string;
  date: string;
  startTime: string;
  durationMinutes: Duration;
}

export function requireSlot(s: WizardSearch): SlotPrereqs {
  if (
    s.practitionerId === undefined ||
    s.date === undefined ||
    s.startTime === undefined ||
    s.durationMinutes === undefined
  ) {
    throw new Error('Missing slot fields');
  }
  return {
    practitionerId: s.practitionerId,
    date: s.date,
    startTime: s.startTime,
    durationMinutes: s.durationMinutes,
  };
}

export interface ModePrereqs extends SlotPrereqs {
  sessionMode: 'online' | 'in_person';
  sessionType: 'new' | 'follow_up';
}

export function requireMode(s: WizardSearch): ModePrereqs {
  const slot = requireSlot(s);
  if (s.sessionMode === undefined) {
    throw new Error('Missing sessionMode');
  }
  return {
    ...slot,
    sessionMode: s.sessionMode,
    sessionType: s.sessionType ?? 'new',
  };
}
